import { Box, Flex, Heading, Skeleton, Stack, Text, useDisclosure, VStack } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useLocalStorage } from "usehooks-ts";

import { getStripeRedirectUrl } from "../../api/api";
import { getProducts, Products } from "../../api/products";
import { getUsage, Usage } from "../../api/usage";
import { capture } from "../../shared/capture";
import { useAppContext } from "../contexts/app";
import { Actions, Analytics, Features } from "../shared/analytics";
import { Button } from "../shared/Button";
import Constants from "../shared/contants";
import DevicesAPIProduct from "./devices-api-product";
import MedicalAPIProduct from "./medical-api-product";
import StripeRedirectAlert from "./stripe-redirect-alert";
import UpcomingInvoice from "./upcoming-invoice";

export default function Billing() {
  const { state } = useAppContext();
  const [isLoading, setIsLoading] = useState(true);
  const [isSandbox] = useLocalStorage<boolean>(Constants.isSandboxKey, false);

  const [usage, setUsage] = useState<Usage | null>(null);
  const [products, setProducts] = useState<Products | null>(null);

  const [isSubscribing, setIsSubscribing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [timer, setTimer] = useState<ReturnType<typeof setTimeout> | null>(null);

  const { isOpen, onOpen, onClose } = useDisclosure({});

  useEffect(() => {
    async function fetchData() {
      if (state.isLoaded) {
        try {
          const products = await getProducts(state.customer.id);
          setProducts(products);

          const usage = await getUsage(state.customer.id);
          if (usage) setUsage(usage);
        } catch (error) {
          capture.error(error, { extra: { context: `billing.products.get` } });
        }
        setIsLoading(false);
      }
    }

    fetchData();
  }, [setIsLoading, state.isLoaded, state.customer]);

  const manageSubscription = async (props?: { subscribeDevices?: boolean }) => {
    const subType = props?.subscribeDevices ? "devices" : "medical";
    Analytics.emit(Actions.manage, Features.subscription, { type: subType });

    setIsSubscribing(true);
    onOpen();

    setTimer(
      // This need to be in settimeout if the user cancels the subscription shouldnt be created
      setTimeout(async () => {
        const redirectUrl = await getStripeRedirectUrl({
          subscribeDevices: props?.subscribeDevices,
        });

        if (!redirectUrl) {
          setErrorMessage(Constants.stripeConnectionFailure);
          onCancel();
          return;
        }

        window.location.href = redirectUrl;
      }, 3000)
    );
  };

  const onCancel = () => {
    timer && clearTimeout(timer);
    onClose();
    Analytics.emit(Actions.cancel, Features.subscription);
    setIsSubscribing(false);
  };

  const clearErrMessage = () => {
    setErrorMessage(null);
  };

  const viewUpcomingInvoices = () => {
    manageSubscription();
    Analytics.emit(Actions.view, Features.invoices);
  };

  if (state.isLoaded) {
    const { medicalStripeSubscriptionItemId, devicesStripeSubscriptionItemId } = state.customer;
    const devicesProduct = products?.products.find(product => product.productCode === "devices");
    const medicalProduct = products?.products.find(product => product.productCode === "medical");

    return (
      <VStack p={10} align="start">
        <Flex
          w={{ base: "full", xl: "49%" }}
          flexDirection={{ base: "column", xl: "row" }}
          justifyContent={"space-between"}
          alignItems={{ base: "flex-start", xl: "center" }}
        >
          <Box mb={3}>
            <Heading mb={2}>Products and billing</Heading>
            <Text fontSize={18}>Manage your product and billing details.</Text>
          </Box>
        </Flex>

        <Skeleton w={"100%"} isLoaded={!isLoading}>
          <Stack direction={{ base: "column", xl: "row" }} spacing="18px" w={"100%"}>
            {devicesProduct && (
              <DevicesAPIProduct
                product={devicesProduct}
                isActive={!!devicesStripeSubscriptionItemId}
                isSubscribing={isSubscribing}
                onSubscribe={manageSubscription}
                isSandbox={isSandbox}
              />
            )}
            {medicalProduct && (
              <MedicalAPIProduct
                product={medicalProduct}
                isActive={!!medicalStripeSubscriptionItemId}
                isSubscribing={isSubscribing}
                onSubscribe={manageSubscription}
                isSandbox={isSandbox}
              />
            )}
          </Stack>
        </Skeleton>

        {(!!devicesStripeSubscriptionItemId || !!medicalStripeSubscriptionItemId) && (
          <Skeleton w={{ base: "full" }} isLoaded={!isLoading}>
            {usage && (
              <>
                <Flex
                  mb={5}
                  flexDirection={{ base: "column", sm: "row" }}
                  alignItems={"center"}
                  justifyContent="space-between"
                >
                  <Heading>Upcoming Invoice</Heading>
                  {!isSandbox && (
                    <Button isLoading={isSubscribing} onClick={viewUpcomingInvoices}>
                      Previous Invoices
                    </Button>
                  )}
                </Flex>
                <UpcomingInvoice usage={usage} />
              </>
            )}
          </Skeleton>
        )}

        <StripeRedirectAlert
          isOpen={isOpen}
          onCancel={onCancel}
          errorMessage={errorMessage}
          onCloseError={clearErrMessage}
        />
      </VStack>
    );
  }

  return null;
}
